import React, { useState } from 'react';
import { processBillPayment } from '../../utils/firestore';
import { useAuth } from '../../contexts/AuthContext';

const UTILITY_PROVIDERS = [
  {
    name: 'Con Edison',
    category: 'Utilities',
    accountFormat: '9999999999',
    website: 'www.coned.com'
  },
  {
    name: 'Comcast Xfinity',
    category: 'Cable / Internet',
    accountFormat: '999999999',
    website: 'www.xfinity.com'
  },
  {
    name: 'Verizon',
    category: 'Wireless',
    accountFormat: '999999999',
    website: 'www.verizon.com'
  },
  {
    name: 'PG&E',
    category: 'Utilities',
    accountFormat: '9999999999',
    website: 'www.pge.com'
  },
  {
    name: 'State Farm',
    category: 'Insurance',
    accountFormat: '999-9999-999',
    website: 'www.statefarm.com'
  }
];

export default function BillPay({ accounts }) {
  const { currentUser } = useAuth();
  const [fromAccount, setFromAccount] = useState('');
  const [provider, setProvider] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [scheduleDate, setScheduleDate] = useState('');
  const [memo, setMemo] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Get tomorrow's date as minimum date for scheduling
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const minDate = tomorrow.toISOString().split('T')[0];

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const commonBills = [
    { name: 'Con Edison', icon: '⚡', provider: 'Con Edison' },
    { name: 'PG&E', icon: '💡', provider: 'PG&E' },
    { name: 'Comcast', icon: '🌐', provider: 'Comcast Xfinity' },
    { name: 'Verizon', icon: '📱', provider: 'Verizon' },
    { name: 'State Farm', icon: '🛡️', provider: 'State Farm' },
    { name: 'Netflix', icon: '🎬', provider: 'Netflix' }
  ];

  const dateStringDaysAgo = (days) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    return d.toISOString().split('T')[0];
  };

  const upcomingBills = [
    {
      provider: 'Con Edison',
      shortName: 'Con Edison',
      icon: '⚡',
      amount: 84.99,
      dueIn: 3,
      accountNumber: '****2345'
    },
    {
      provider: 'PG&E',
      shortName: 'PG&E',
      icon: '💡',
      amount: 45.5,
      dueIn: 5,
      accountNumber: '****7890'
    },
    {
      provider: 'Comcast Xfinity',
      shortName: 'Comcast',
      icon: '🌐',
      amount: 79.99,
      dueIn: 7,
      accountNumber: '****4321'
    }
  ];

  const paymentHistory = [
    {
      provider: 'Con Edison',
      shortName: 'Con Edison',
      amount: 84.99,
      date: dateStringDaysAgo(14),
      status: 'Paid',
      confirmationNumber: 'CE123456'
    },
    {
      provider: 'Verizon',
      shortName: 'Verizon',
      amount: 75.0,
      date: dateStringDaysAgo(7),
      status: 'Paid',
      confirmationNumber: 'VZ789012'
    },
    {
      provider: 'Comcast Xfinity',
      shortName: 'Comcast',
      amount: 79.99,
      date: dateStringDaysAgo(21),
      status: 'Paid',
      confirmationNumber: 'XF345678'
    }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess(false);

    if (!fromAccount || !provider || !accountNumber || !amount || !scheduleDate) {
      setError('Please fill in all required fields');
      return;
    }

    const paymentAmount = parseFloat(amount);
    if (isNaN(paymentAmount) || paymentAmount <= 0) {
      setError('Please enter a valid amount');
      return;
    }

    const selectedAccount = accounts.find(acc => acc.type === fromAccount);
    if (paymentAmount > selectedAccount.available) {
      setError('Insufficient funds');
      return;
    }

    setLoading(true);
    try {
      await processBillPayment({
        fromAccount,
        provider,
        accountNumber,
        amount: paymentAmount,
        scheduleDate,
        memo,
        userId: currentUser.uid
      });

      // Reset form
      setProvider('');
      setAccountNumber('');
      setAmount('');
      setScheduleDate('');
      setMemo('');
      setSuccess(true);
    } catch (error) {
      setError('Payment failed. Please try again.');
      console.error('Bill payment error:', error);
    }
    setLoading(false);
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Bills</h3>
      
      {success && (
        <div className="mb-4 p-4 bg-green-100 text-green-700 rounded">
          Payment scheduled successfully!
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div>
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                {error}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                From Account
              </label>
              <select
                value={fromAccount}
                onChange={(e) => setFromAccount(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              >
                <option value="">Select account</option>
                {accounts.map((account) => (
                  <option key={account.accountNumber} value={account.type}>
                    {account.type} - {formatCurrency(account.available)}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Common Providers
              </label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {commonBills.map((bill) => (
                  <button
                    key={bill.name}
                    type="button"
                    onClick={() => setProvider(bill.provider)}
                    className={`p-3 rounded-xl text-sm font-medium transition-all duration-200 flex flex-col items-center justify-center
                      ${provider === bill.provider 
                        ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500 ring-offset-2' 
                        : 'bg-gray-50 text-gray-700 hover:bg-emerald-50'}`}
                  >
                    <span className="text-xl mb-1">{bill.icon}</span>
                    <span className="text-xs text-center">{bill.name}</span>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Account Number
              </label>
              <input
                type="text"
                value={accountNumber}
                onChange={(e) => setAccountNumber(e.target.value)}
                placeholder="Enter account number"
                className="w-full p-2 border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount
              </label>
              <input
                type="number"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                placeholder="Enter amount"
                className="w-full p-2 border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
                min="0.01"
                step="0.01"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Date
              </label>
              <input
                type="date"
                value={scheduleDate}
                onChange={(e) => setScheduleDate(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
                min={minDate}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Memo (Optional)
              </label>
              <input
                type="text"
                value={memo}
                onChange={(e) => setMemo(e.target.value)}
                placeholder="Add a memo"
                className="w-full p-2 border border-gray-300 rounded focus:ring-primary-500 focus:border-primary-500"
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className={`w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-white bg-primary-700 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 ${
                loading ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              disabled={loading}
            >
              {loading ? 'Processing…' : 'Schedule payment'}
            </button>
          </form>
        </div>

        <div className="space-y-6">
          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Upcoming Bills</h3>
            <div className="space-y-3">
              {upcomingBills.map((bill, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow-sm">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                        <span className="text-lg">{bill.icon}</span>
                      </div>
                      <div>
                        <p className="font-medium text-gray-900">{bill.provider}</p>
                        <div className="flex items-center text-sm space-x-2">
                          <span className="text-emerald-600">Due in {bill.dueIn} days</span>
                          <span className="text-gray-400">•</span>
                          <span className="text-gray-500">Acct: {bill.accountNumber}</span>
                        </div>
                      </div>
                    </div>
                    <span className="font-medium text-gray-900">${bill.amount.toFixed(2)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-6">
            <h3 className="text-sm font-medium text-gray-900 mb-4">Payment History</h3>
            <div className="space-y-3">
              {paymentHistory.map((payment, index) => (
                <div key={index} className="flex items-center justify-between text-sm p-2 hover:bg-white rounded-lg transition-colors duration-200">
                  <div className="flex flex-col">
                    <span className="text-gray-900 font-medium">{payment.provider}</span>
                    <span className="text-gray-500 text-xs">
                      Conf: {payment.confirmationNumber}
                    </span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <span className="text-gray-900">${payment.amount.toFixed(2)}</span>
                    <span className="px-2 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                      {payment.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
} 